import { useCallback, useEffect } from "react";

import { useLazyQuery, useSubscription } from "@apollo/client/react";
import { getEnvConfig } from "../lib/config/config";
import { Order } from "../models/order";
import { ORDERS_QUERY, ORDERS_SUBSCRIPTION } from "../queries/order";
import { useRadarDispatch, useRadarState } from "../reducers/reducers";

import { SET_ORDERS, UPDATE_ORDER } from "../reducers/actions";

export type OrderQueryRes = { orders: Order[] };
export type OrderSubRes = { orderUpdated: Order };
export type OrderQueryVars = { locationNumber: string };

export const useOrderFetch = () => {
  const {
    selectedLocation,
    user,
    settings: { orderRefetchIntervalInMS },
  } = useRadarState();
  const dispatch = useRadarDispatch();

  // Using no-cache because we only do a full query when we want the full set of data (e.g., when a new location is loaded).
  // In Apollo Client 4, notifyOnNetworkStatusChange defaults to true, which would update `loading`/`networkStatus`
  // on every periodic refetch below and trigger unnecessary re-renders. Keep it false so `loading` reflects only
  // the initial fetch and background polls don't churn the UI.
  const [getOrders, { error, loading, data }] = useLazyQuery<
    OrderQueryRes,
    OrderQueryVars
  >(ORDERS_QUERY, {
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: false,
  });

  // When the location or authentication changes, fetch orders for new location
  const callGetOrders = useCallback(() => {
    if (
      user != null &&
      selectedLocation != null &&
      selectedLocation.locationNumber !== ""
    ) {
      getOrders({
        variables: { locationNumber: selectedLocation.locationNumber },
      });
    }
  }, [getOrders, selectedLocation, user]);

  useEffect(() => {
    callGetOrders();

    // Set up timer to periodically refetch all data to insure data is in sync
    const intervalId = setInterval(() => {
      callGetOrders();
    }, orderRefetchIntervalInMS);

    return () => {
      clearInterval(intervalId);
    };
  }, [callGetOrders, orderRefetchIntervalInMS]);

  // Set up a subscription to listen for changes to orders and update state.
  // With GraphQL.useSubscriptions off this never executes, so graphql-ws (which
  // connects lazily, on first subscribe) never opens a socket and the periodic
  // refetch above is the only source of updates.
  // NOTE: React strict mode (in development env only) has a side-effect of
  // double-subscribing.
  useSubscription<OrderSubRes>(ORDERS_SUBSCRIPTION, {
    skip: !getEnvConfig().GraphQL.useSubscriptions || selectedLocation == null,
    variables: { locationNumber: selectedLocation?.locationNumber },
    onData: ({ data }) => {
      const updatedOrder = data.data?.orderUpdated;

      if (updatedOrder) {
        dispatch({ type: UPDATE_ORDER, order: updatedOrder });
      }
    },
  });

  // When the data object is updated by the query, set the state
  useEffect(() => {
    dispatch({
      type: SET_ORDERS,
      orders: {
        loading,
        error,
        orders: data?.orders,
      },
    });

    // eslint-disable-next-line
  }, [error, loading, data?.orders]);
};
